import React, { useState } from "react";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DateRange } from "react-date-range";
import * as XLSX from "xlsx";

import { useAuth } from "../AuthProvider";
import { useMsal } from "@azure/msal-react";
import urlconfig from "../config/urlconfig";
import { getApiToken } from "../authUtils";
import { formatCurrency } from "../util";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const ExportByDate = () => {
    const { account } = useAuth();
    const { instance } = useMsal();

    const [dateModal, setDateModal] = useState(false);
    const [loading, setLoading] = useState(false);

    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: "selection"
        }
    ]);

    const handleOpenModal = () => setDateModal(true);
    const handleCloseModal = () => setDateModal(false);

    const clearDateFilter = () => {
        setDateRange([
            {
                startDate: new Date(),
                endDate: new Date(),
                key: "selection"
            }
        ]);
    };

    const fetchPage = async (page, size = 500) => {
        try {
            const accessToken = await getApiToken(instance, account);

            const res = await fetch(
                `${urlconfig.backend_api_base_url}/api/v2/items?page=${page}&size=${size}`,
                {
                    headers: { Authorization: `Bearer ${accessToken}` }
                }
            );

            if (!res.ok) throw new Error("Failed to fetch data");
            return await res.json();
        } catch (err) {
            console.error(err);
            return null;
        }
    };
    const fetchAllPages = async () => {
        let page = 0;
        let totalPages = 1;
        let allItems = [];

        setLoading(true);

        while (page < totalPages) {
            const data = await fetchPage(page, 500);
            if (!data) break;

            allItems.push(...(data.content || []));
            totalPages = data.totalPages || 1;

            page++;
            await new Promise((res) => setTimeout(res, 50));
        }

        setLoading(false);
        return allItems;
    };

    const exportByDate = async () => {
        // handleCloseModal();
        setLoading(true)

        const start = new Date(dateRange[0].startDate);
        const end = new Date(dateRange[0].endDate);

        const allItems = await fetchAllPages();

        const filtered = allItems.filter((item) => {
            const created = new Date(item.createdAT);

            const start = new Date(dateRange[0].startDate);
            const end = new Date(dateRange[0].endDate);
            end.setHours(23, 59, 59, 999);

            return created >= start && created <= end;
        });

        if (filtered.length === 0) {
            alert("No data found for selected date range");
            return;
        }

        const excelRows = filtered.map((item) => {
            const spWithGst = item.items?.reduce(
                (acc, x) => acc + x.qty * (x.sp * (1 + (x.gst || 0) / 100)),
                0
            );

            const ppWithGst = item.items?.reduce(
                (acc, x) => acc + x.qty * (x.pp * (1 + (x.gst || 0) / 100)),
                0
            );

            const tPp = Number(item.transportationTotalPp || 0);
            const tSp = Number(item.transportationTotalSp || 0);
            const cPp = Number(item.customizationTotalPp || 0);
            const cSp = Number(item.customizationTotalSp || 0);
            const mPp = Number(item.marketingTotalPp || 0);
            const mSp = Number(item.marketingTotalSp || 0);

            const customPP = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalPp) || 0), 0);
            const customSP = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalSp) || 0), 0);
            const customPPWithGst = (item.customRows || []).reduce((s, r) => {
            const pp = parseFloat(r.customTotalPp) || 0;
            const gst = parseFloat(r.customGST) || 0;
            return s + pp * (1 + gst / 100);
            }, 0);
            const customSPWithGst = (item.customRows || []).reduce((s, r) => {
            const sp = parseFloat(r.customTotalSp) || 0;
            const gst = parseFloat(r.customGST) || 0;
            return s + sp * (1 + gst / 100);
            }, 0);
            const tGst = Number(item.costSummary?.transportationGST || 0);
            const cGst = Number(item.costSummary?.customizationGST  || 0);
            const mGst = Number(item.costSummary?.marketingGST      || 0);
            const totalPp = ppWithGst + tPp * (1 + tGst / 100) + cPp * (1 + cGst / 100) + mPp * (1 + mGst / 100) + customPPWithGst;
            const totalSp = spWithGst + tSp * (1 + tGst / 100) + cSp * (1 + cGst / 100) + mSp * (1 + mGst / 100) + customSPWithGst;
            const marginVal       = totalSp - totalPp;

            const ppWithoutGST = item.items?.reduce(
                (acc, x) => acc + x.qty * x.pp,
                0
            );

            const spWithoutGST = item.items?.reduce(
                (acc, x) => acc + x.qty * x.sp,
                0
            );

            const totalPPExclGST = ppWithoutGST + tPp + cPp + mPp + customPP;
            const totalSPExclGST = spWithoutGST + tSp + cSp + mSp + customSP;

            const marginValExclGST =  totalSPExclGST - totalPPExclGST

            const poNumbers = (item.items || []).map((i) => i.poNumber).join(", ");
            const details = (item.items || []).map((i) => i.details).join(", ");
            // Same order as Details; a line without custom details shows "-" so the two lists line up.
            // Lines are split with " | " because custom details often contain commas
            const customDetails = (item.items || []).some((i) => i.customDetails?.trim())
                ? (item.items || []).map((i) => i.customDetails?.trim() || "-").join(" | ")
                : "";
            const brands = (item.items || []).map((i) => i.brand?.name || "N/A").join(", ");
            const categories = (item.items || []).map((i) => i.category?.name || "N/A").join(", ");
            const subcategories = (item.items || []).map((i) => i.subcategory?.name || "N/A").join(", ");

            return {
                Date: item.date ? new Date(item.date).toLocaleDateString("en-IN") : "",
                "Creation Date": item.createdAT ? new Date(item.createdAT).toLocaleString("en-IN") : "",
                "Cost Sheet For": item.costsheetfor || "",
                "Client Name": item.clientName || "",
                "Sales Order No": item.salesOrderNo || "",
                "SCM SPOC": item.scmspoc || "",
                "Sales SPOC": item.salesPOC || "",
                Details: details,
                "Custom Details": customDetails,
                Brand: brands,
                Category: categories,
                Subcategory: subcategories,
                "Delivery Date": item.deliveryDate ? new Date(item.deliveryDate).toLocaleDateString("en-IN") : "",
                "Total PP (Incl GST)": formatCurrency(totalPp),
                "Total SP (Incl GST)": formatCurrency(totalSp),
                "Total PP (Excl GST)": formatCurrency(totalPPExclGST),
                "Total SP (Excl GST)": formatCurrency(totalSPExclGST),
                "Margin (Excl GST)": formatCurrency(marginValExclGST),
                "Transportation PP": formatCurrency(tPp),
                "Transportation SP": formatCurrency(tSp),
                "Customization PP": formatCurrency(cPp),
                "Customization SP": formatCurrency(cSp),
                "Marketing PP": formatCurrency(mPp),
                "Marketing SP": formatCurrency(mSp),
                "PO Number": poNumbers,
                "Created By": item.createdBy || "",
                "Approval Status":
                    item.pending === "true"
                        ? `Approved By ${item.approvedBy?.join(", ")}`
                        : item.pending === "false"
                            ? `Rejected: ${item.approvedBy?.join(", ")} — Note: ${item.remarks}`
                            : "Pending",
                "Order Status": item.orderStatus || ""
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(excelRows);

        Object.keys(worksheet).forEach((cell) => {
            if (!cell.startsWith("!")) {
                worksheet[cell].s = {
                    border: {
                        top: { style: "thin" },
                        bottom: { style: "thin" },
                        left: { style: "thin" },
                        right: { style: "thin" }
                    }
                };
            }
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Filtered_Data");

        XLSX.writeFile(
            workbook,
            `Filtered_Cost_Sheets_${start.toLocaleDateString("en-IN")}_to_${end.toLocaleDateString("en-IN")}.xlsx`
        );
        setLoading(false)
        handleCloseModal();
    };

    return (
        <>
            <Button
                variant="contained"
                color="secondary"
                onClick={handleOpenModal}
            >
                <FileDownloadOutlinedIcon sx={{ mr: 1 }} />
                Export by Date
            </Button>

            <Dialog open={dateModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                
                <DialogTitle>Select Date Range</DialogTitle>

                <DialogContent>
                    <Box display="flex" justifyContent="center">
                        <DateRange
                            editableDateInputs={true}
                            onChange={(item) => setDateRange([item.selection])}
                            moveRangeOnFirstSelection={false}
                            ranges={dateRange}
                            direction="vertical"
                        />
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={clearDateFilter} color="secondary">
                        Clear
                    </Button>
                    <Button
                        onClick={exportByDate}
                        variant="contained"
                        color="primary"
                        disabled={loading}
                    >
                        {loading ? "Exporting..." : "Export Data"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ExportByDate;
